import {faCheckCircle} from '@fortawesome/pro-solid-svg-icons'
import {AnimatePresence, motion} from 'framer-motion'
import {forwardRef} from 'react'

import {twMerge} from '@sdox/utils'

import {FaIcon, Headline} from '../..'
import {LazyImage} from '../LazyImage'

import type {LazyImageProps} from '../LazyImage'
import type {ClassNameValue} from 'tailwind-merge'
import type {ComponentProps, ReactNode} from 'react'
import type {Variants} from 'framer-motion'

export interface TeaserProps extends ComponentProps<'div'> {
  active?: boolean
  contentClassName?: ClassNameValue
  withChildren?: boolean
  title: string
  actionNode?: ReactNode
  imageClickOnly?: boolean
  imageClassName?: ClassNameValue
  keyVisual: LazyImageProps
  activeIndicatorPosition?: 'none' | 'bottom' | 'left'
  withCheckbox?: boolean
}

export const Teaser = forwardRef<HTMLDivElement, TeaserProps>(
  (
    {
      title,
      keyVisual,
      className,
      children,
      actionNode,
      imageClickOnly = false,
      imageClassName,
      contentClassName,
      withChildren = false,
      active = false,
      withCheckbox = false,
      activeIndicatorPosition = 'bottom',
      onClick,
      ...restProps
    },
    ref,
  ) => {
    const borderClasses: Record<typeof activeIndicatorPosition, string> = {
      bottom: 'top-full h-1 w-full',
      left: 'top-0 h-full w-1',
      none: '',
    }

    const variants: Variants = {
      visible: {
        opacity: 1,
      },
      hidden: {
        opacity: 0,
      },
    }

    return (
      <div
        ref={ref}
        data-cy={'teaser'}
        className={twMerge(
          'relative flex select-none flex-col bg-surface-primary',
          active && 'shadow-lg',
          !imageClickOnly && 'cursor-pointer',
          className,
        )}
        onClick={imageClickOnly ? undefined : onClick}
        {...restProps}
      >
        <AnimatePresence>
          {active && (
            <motion.div
              initial={'hidden'}
              animate={'visible'}
              exit={'hidden'}
              variants={variants}
              transition={{
                duration: 0.5,
                ease: 'linear',
              }}
              className={twMerge('absolute bg-interactive-surface-filled-brand-default', borderClasses[activeIndicatorPosition])}
            />
          )}
        </AnimatePresence>
        <div
          className={twMerge('bg-gray-300', imageClassName)}
          onClick={imageClickOnly ? onClick : undefined}
        >
          <LazyImage
            src={keyVisual.src}
            placeholderSrc={keyVisual.placeholderSrc}
            alt={'keyVisual'}
            // this height and width sould match the requested thumbnail.
            // We need to cover because crop2 is calculated after the "real" crop and therefore
            // the dimensions may not be 1:1 the requested ones
            // This is only a problem on fixed h/w calls
            className={twMerge('size-full object-cover object-center', onClick && 'cursor-pointer')}
          />
        </div>
        <div className={twMerge('bg-white p-6', contentClassName)}>
          <div className={'flex items-center justify-between'}>
            <Headline
              size={4}
              className={'line-clamp-1 break-all'}
            >
              {title}
            </Headline>
            {withCheckbox && (
              <FaIcon
                icon={faCheckCircle}
                className={twMerge('size-8 text-interactive-glyph-brand-default', active ? 'visible' : 'invisible')}
              />
            )}
            {actionNode}
          </div>
          {withChildren && children && <div className={'mt-2'}>{children}</div>}
        </div>
      </div>
    )
  },
)
